import React, { useState } from 'react';
import ShowingSchedule from '../../../components/ShowingSchedule';
import styled from 'styled-components';

const Form = styled.form`
  max-width: 600px;
  margin: 0 auto;
`;

const FormSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  border: 1px solid #f0f0f0;
`;

const SectionHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f8f9fa;
  
  h3 {
    margin: 0 0 4px 0;
    color: #333;
    font-size: 18px;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  &.single-column {
    grid-template-columns: 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const SubmitButton = styled.button`
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  width: 100%;
  
  &:hover {
    background: #0056b3;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const PropertyForm = ({ property, onSubmit, isEditing = false }) => {
  const [formData, setFormData] = useState({
    // Property information
    address_line1: property?.address_line1 || '',
    unit_number: property?.unit_number || '',
    city: property?.city || '',
    state: property?.state || 'CA',
    zip_code: property?.zip_code || '',
    private_showing_instructions: property?.private_showing_instructions || '',
    
    // Scheduling availability
    availability_type: property?.availability_type || 'default',
    custom_availability: property?.custom_availability || {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: true, start: '09:00', end: '17:00' },
      sunday: { enabled: true, start: '09:00', end: '17:00' }
    },
    
    // Default settings
    status: 'listed',
    booking_approval: 'automatic'
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const submitData = {
        ...formData,
        // Ensure required fields have values
        full_address: `${formData.address_line1}${formData.unit_number ? `, ${formData.unit_number}` : ''}, ${formData.city}, ${formData.state} ${formData.zip_code}`
      };
      
      console.log('📋 PropertyForm submitting simplified data:', submitData);
      
      await onSubmit(submitData);
      
      if (!isEditing) {
        // Reset form after successful submission
        setFormData({
          address_line1: '',
          unit_number: '',
          city: '',
          state: 'CA',
          zip_code: '',
          private_showing_instructions: '',
          status: 'listed',
          booking_approval: 'automatic',
          occupancy_status: 'vacant',
          use_public_profile: true
        });
      }
    } catch (error) {
      console.error('Error submitting property:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Property Address Section */}
      <FormSection>
        <SectionHeader>
          <h3>🏠 Property Information</h3>
          <p>Enter the basic property details. You can add contacts after creating the property.</p>
        </SectionHeader>
        
        <FormGrid>
          <FormField className="full-width">
            <Label>Street Address *</Label>
            <Input
              type="text"
              name="address_line1"
              value={formData.address_line1}
              onChange={handleChange}
              placeholder="123 Main Street"
              required
            />
          </FormField>
          
          <FormField>
            <Label>Unit Number</Label>
            <Input
              type="text"
              name="unit_number"
              value={formData.unit_number}
              onChange={handleChange}
              placeholder="Apt 2B, Unit 5, etc."
            />
          </FormField>
          
          <FormField>
            <Label>City *</Label>
            <Input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="San Francisco"
              required
            />
          </FormField>
          
          <FormField>
            <Label>State *</Label>
            <Select
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
            >
              <option value="">Select State</option>
              <option value="AL">Alabama</option>
              <option value="AK">Alaska</option>
              <option value="AZ">Arizona</option>
              <option value="AR">Arkansas</option>
              <option value="CA">California</option>
              <option value="CO">Colorado</option>
              <option value="CT">Connecticut</option>
              <option value="DE">Delaware</option>
              <option value="FL">Florida</option>
              <option value="GA">Georgia</option>
              <option value="HI">Hawaii</option>
              <option value="ID">Idaho</option>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="IA">Iowa</option>
              <option value="KS">Kansas</option>
              <option value="KY">Kentucky</option>
              <option value="LA">Louisiana</option>
              <option value="ME">Maine</option>
              <option value="MD">Maryland</option>
              <option value="MA">Massachusetts</option>
              <option value="MI">Michigan</option>
              <option value="MN">Minnesota</option>
              <option value="MS">Mississippi</option>
              <option value="MO">Missouri</option>
              <option value="MT">Montana</option>
              <option value="NE">Nebraska</option>
              <option value="NV">Nevada</option>
              <option value="NH">New Hampshire</option>
              <option value="NJ">New Jersey</option>
              <option value="NM">New Mexico</option>
              <option value="NY">New York</option>
              <option value="NC">North Carolina</option>
              <option value="ND">North Dakota</option>
              <option value="OH">Ohio</option>
              <option value="OK">Oklahoma</option>
              <option value="OR">Oregon</option>
              <option value="PA">Pennsylvania</option>
              <option value="RI">Rhode Island</option>
              <option value="SC">South Carolina</option>
              <option value="SD">South Dakota</option>
              <option value="TN">Tennessee</option>
              <option value="TX">Texas</option>
              <option value="UT">Utah</option>
              <option value="VT">Vermont</option>
              <option value="VA">Virginia</option>
              <option value="WA">Washington</option>
              <option value="WV">West Virginia</option>
              <option value="WI">Wisconsin</option>
              <option value="WY">Wyoming</option>
            </Select>
          </FormField>
          
          <FormField>
            <Label>ZIP Code</Label>
            <Input
              type="text"
              name="zip_code"
              value={formData.zip_code}
              onChange={handleChange}
              placeholder="94102"
              pattern="[0-9]{5}(-[0-9]{4})?"
            />
          </FormField>
        </FormGrid>
      </FormSection>

      {/* Showing Schedule & Instructions */}
      <FormSection>
        <SectionHeader>
          <h3>📅 Showing Schedule & Instructions</h3>
          <p>Set availability and instructions for property showings</p>
        </SectionHeader>
        
        <ShowingSchedule
          schedule={formData.custom_availability}
          onChange={(newSchedule) => setFormData({
            ...formData,
            custom_availability: newSchedule
          })}
        />
        
        <FormField style={{ marginTop: '24px' }}>
          <Label>📋 Private Showing Instructions</Label>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
            🔒 These instructions are only shown to confirmed buyers and included in confirmation emails. Not visible on public booking page.
          </p>
          <TextArea
            name="private_showing_instructions"
            value={formData.private_showing_instructions}
            onChange={handleChange}
            placeholder="Example: 2 large dogs - will be secured in garage. Do not open garage. Door code 5555 if tenant not home. Lockbox on front gate, combo ABC123."
          />
        </FormField>
      </FormSection>

      <SubmitButton type="submit" disabled={submitting}>
        {submitting ? 'Creating Property...' : isEditing ? 'Update Property' : '🏠 Create Property'}
      </SubmitButton>
    </Form>
  );
};

export default PropertyForm;